/**
 * MarketplaceDetailModal — modeled on the GPT marketplace detail modal.
 *
 * Layout (top-down):
 *   - Big icon
 *   - Name + author + (optional) official badge
 *   - Tagline / description
 *   - Stats row: rating + install count
 *   - Capabilities (checkmarked bullet list)
 *   - Conversation Starters (chips)
 *   - Ratings histogram (5-star bars)
 *   - "More by <author>" — optional, only when present (we don't fetch
 *     these here in the first cut to keep the modal a single round-trip;
 *     can be added behind a useEffect later)
 *   - Fixed bottom CTA "Deploy to <projectName>"
 *
 * Accessibility:
 *   - role=dialog + aria-modal=true
 *   - focus trapped between the close button and the deploy button
 *   - Esc closes
 *   - Background click closes (with stopPropagation on the modal body)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Marketplace from '../../services/marketplace';
import { IonIcon } from '@ionic/react';
import {
  closeOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  star,
  starOutline,
  cloudUploadOutline,
  exitOutline,
} from 'ionicons/icons';
import MarketplaceWorkflowDeployFooter from './MarketplaceWorkflowDeployFooter';
import GitlabConnectModal from '../../components/integrations/GitlabConnectModal';
import LarkConnectModal from '../../components/integrations/LarkConnectModal';
import { useStartOAuth } from '../../hooks/useIntegrations';
import './MarketplaceDetailModal.css';

// Marker the popup-done page posts back to the opener after OAuth
// completes. Defined here (not magic-stringed) so both the listener
// and the popup-done.html stay in sync if we ever rename it.
const OAUTH_POPUP_DONE_PATH = '/oauth-popup-done.html';
const OAUTH_SUCCESS_EVENT = 'zibby:oauth-success';

const MarketplaceDetailModal = ({
  entry,
  projectId,
  projectName,
  isOpen,
  onClose,
  onDeploy,
  deploying,
  // Click handler invoked when the user picks a star count, signature:
  //   onRate(entry, n) — n is 1..5. Should POST the rating and resolve
  //   to the refreshed entry so the modal can update its histogram.
  //   When omitted, the stars stay non-interactive (display-only).
  onRate,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Integration status — fetched lazily on open so we only spend the
  // round-trip when the modal is actually visible. null = not loaded
  // yet; { required:[], ... } = loaded; error = string when fetch failed.
  // Loading flag drives the skeleton.
  const [intStatus, setIntStatus] = useState(null);
  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState(null);
  // `refreshKey` is bumped after a successful Connect (OAuth popup OR
  // GitLab/Lark form submit) to force the integration-status fetch
  // useEffect to re-run. Cheaper + clearer than passing a refetch
  // function around.
  const [refreshKey, setRefreshKey] = useState(0);
  // Per-provider form modal open flags. These render the SAME
  // GitlabConnectModal / LarkConnectModal that /integrations uses —
  // user fills creds in-place, modal closes on success, refreshKey
  // bumps so the checklist re-fetches.
  const [gitlabModalOpen, setGitlabModalOpen] = useState(false);
  const [larkModalOpen, setLarkModalOpen] = useState(false);

  // Per-provider OAuth-start mutations. Each is a thin wrapper around
  // `GET /<provider>/oauth/start?returnTo=…`; mutate() returns
  // { authUrl } that we open in a popup.
  const startGithubOAuth = useStartOAuth('github');
  const startJiraOAuth   = useStartOAuth('jira');
  const startSlackOAuth  = useStartOAuth('slack');
  const startSentryOAuth = useStartOAuth('sentry');
  const OAUTH_HOOKS = {
    github: startGithubOAuth,
    jira:   startJiraOAuth,
    slack:  startSlackOAuth,
    sentry: startSentryOAuth,
  };

  useEffect(() => {
    if (!isOpen || !entry?.slug || !projectId) {
      setIntStatus(null);
      setIntError(null);
      return undefined;
    }
    let cancelled = false;
    // Only show the loading skeleton on the FIRST fetch (when we
    // don't have any data yet). Subsequent refreshes (refreshKey bump
    // after a successful Connect) silently update in the background
    // — React's keyed-list diff updates only the changed row so the
    // user sees "Jira: Connect" → "Jira: Connected" cleanly, with
    // GitHub + GitLab rows untouched. Without this gate the whole
    // section would skeleton-flash on every Connect.
    if (refreshKey === 0) {
      setIntLoading(true);
    }
    setIntError(null);
    Marketplace.getIntegrationStatus(entry.slug, projectId)
      .then((res) => {
        if (cancelled) return;
        setIntStatus(res || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful degrade — modal stays usable, deploy stays clickable.
        // The checklist section just won't render.
        setIntError(err?.message || 'Failed to load integration status');
      })
      .finally(() => {
        if (!cancelled) setIntLoading(false);
      });
    return () => { cancelled = true; };
  // refreshKey included so a successful Connect (OAuth or form modal)
  // forces a refetch without the skeleton flash (handled above).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entry?.slug, projectId, refreshKey]);

  // Listen for the popup-done page postMessage and refetch integration
  // status. We accept messages from any origin matching this window's
  // (the popup-done page is same-origin); the popup-done page already
  // targets `window.location.origin` when posting, so cross-origin
  // messages get dropped by the browser regardless.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMessage = (e) => {
      if (e?.data?.type === OAUTH_SUCCESS_EVENT) {
        setRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isOpen]);

  // Dispatch a Connect click to the right flow per provider.
  //   github / jira / slack / sentry  → OAuth via popup
  //   gitlab                          → inline GitlabConnectModal (form)
  //   lark                            → inline LarkConnectModal (form)
  //   anything else                   → fall through to /integrations
  //                                     (defensive — keeps unknown
  //                                     providers reachable instead of
  //                                     silently no-op'ing)
  const handleConnectClick = useCallback((providerId, fallbackPath) => {
    if (providerId === 'gitlab') {
      setGitlabModalOpen(true);
      return;
    }
    if (providerId === 'lark') {
      setLarkModalOpen(true);
      return;
    }
    const hook = OAUTH_HOOKS[providerId];
    if (!hook) {
      // Unknown provider — preserve the legacy behavior of navigating
      // to the canonical /integrations page (or whatever connectPath
      // the backend supplied).
      if (fallbackPath) window.location.href = fallbackPath;
      return;
    }
    hook.mutate(
      { returnTo: OAUTH_POPUP_DONE_PATH },
      {
        onSuccess: (data) => {
          // Open the OAuth URL in a small popup so the marketplace
          // modal stays visible behind it. Popup posts back when done.
          // Fallback to same-tab redirect when the browser blocks the
          // popup (e.g. mobile Safari with strict pop-up settings).
          const popup = window.open(
            data.authUrl,
            'zibby-oauth',
            'popup=1,width=640,height=720,scrollbars=yes',
          );
          if (!popup) {
            window.location.href = data.authUrl;
          }
        },
        // Errors surface via the integration card UI on next refetch —
        // intentionally not raising a toast here to keep the modal
        // focused. The console log helps when debugging.
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error(`[marketplace] Failed to start ${providerId} OAuth:`, err);
        },
      },
    );
  // OAUTH_HOOKS isn't a stable reference but the mutate functions
  // inside it are (react-query memoizes per service); fine for the
  // intent here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Esc to close + focus trap ────────────────────────────────────
  // Lightweight focus trap: Tab/Shift-Tab cycles between the first
  // and last focusable element inside the dialog. Avoids pulling in a
  // dedicated trap-focus library; the focusable set inside this modal
  // is small enough (close button, chips for keyboard activation,
  // deploy button) that this hand-rolled cycle is fine.
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;

    const previouslyFocused = document.activeElement;
    // Defer focus until paint so the open animation doesn't yank
    // attention from the user's last action.
    const t = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  // v1/v2 public-mode click: parent's handleDeploy bounces to /connect
  // (unauth) or opens the wizard (authed). No name + warm-pool here;
  // the wizard collects those after the project is picked.
  const handlePublicDeploy = () => {
    if (deploying || !entry) return;
    if (blockedByMissing) { flagMissingThenAbort(); return; }
    onDeploy?.(entry, {});
  };

  // Deploy-gate state. When the user clicks Deploy with one or more
  // required integrations missing, we block the call and surface a
  // banner above the footer telling them exactly which integration(s)
  // to connect. The banner also flashes the integration section so the
  // user's eye lands there.
  const [blockReason, setBlockReason] = useState(null);
  const intSectionRef = useRef(null);
  const missingLabels = React.useMemo(() => {
    if (!intStatus || !Array.isArray(intStatus.missing)) return [];
    return intStatus.missing.map((entry) => {
      if (typeof entry === 'string') {
        return intStatus.integrations?.[entry]?.name || entry;
      }
      const members = Array.isArray(entry?.any) ? entry.any : [];
      return members.map((id) => intStatus.integrations?.[id]?.name || id).join(' or ');
    });
  }, [intStatus]);
  const blockedByMissing = missingLabels.length > 0;

  const flagMissingThenAbort = () => {
    setBlockReason(`Connect ${missingLabels.join(' and ')} before deploying.`);
    intSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  // v3 in-project deploy: delegate to MarketplaceWorkflowDeployFooter,
  // which owns the name field + validation + warm-pool menu. It
  // surfaces just { workflowName, warm } and we wrap with the entry
  // before passing to the parent's onDeploy.
  const handleV3Deploy = (opts) => {
    if (blockedByMissing) { flagMissingThenAbort(); return; }
    onDeploy?.(entry, opts);
  };

  // Rating state — derived from the entry but ephemeral on hover so the
  // user can preview the star count before committing. `submittingRating`
  // disables clicks during the inflight POST.
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  if (!isOpen || !entry) return null;

  const histogram = entry.ratingHistogram || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const histogramTotal = [1, 2, 3, 4, 5].reduce((a, k) => a + (histogram[k] || 0), 0);
  const rating = Number(entry.ratingAvg) || 0;
  const myRatingValue = Number(entry.myRating?.rating) || 0;
  // Public-marketplace mode: no project context yet. The Deploy click is
  // a bounce — parent navigates to /connect (unauth) or /projects with
  // ?deployTemplate=<slug> (authed but no project picked). In this mode
  // the workflow-name input is meaningless (no namespace to validate
  // against) so we hide it and simplify the footer.
  const isPublic = !projectId;
  // Stars filled = hover (live preview) > my prior rating > rounded avg.
  // Showing "my rating" by default makes the affordance obvious (the
  // stars look "set") and matches App Store / GitHub patterns.
  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
  const canRate = typeof onRate === 'function';

  const deployLabel = projectName
    ? `Deploy to ${projectName}`
    : 'Deploy';

  // Simple Icons CDN slugs per backend integration id. Anything not in
  // this map falls back to the integration name (text). The CDN serves
  // each SVG in the brand's official color when no color suffix is given,
  // which is what we want — except for brands whose brand color is too
  // dark to read on the modal's dark surface (e.g. GitHub = #181717).
  // For those we override to a near-white hex via the `/<hex>` suffix.
  // Reference: https://simpleicons.org/  /  https://cdn.simpleicons.org/<slug>/<hex?>
  const BRAND_SLUG = {
    github: 'github',
    gitlab: 'gitlab',
    jira: 'jira',
    sentry: 'sentry',
    slack: 'slack',
    lark: 'larksuite',
  };
  const BRAND_DARK_OVERRIDE = {
    github: 'e8e8e8',
  };
  const BrandLogo = ({ id, name }) => {
    const slug = BRAND_SLUG[id];
    // Unknown provider: just the name (no logo to pair with — degrades
    // gracefully when simpleicons doesn't have the brand).
    if (!slug) return <span className="mkt-modal-integration-name">{name}</span>;
    const override = BRAND_DARK_OVERRIDE[id];
    const src = override
      ? `https://cdn.simpleicons.org/${slug}/${override}`
      : `https://cdn.simpleicons.org/${slug}`;
    // Logo + name pair: keeps the row readable when a user has multiple
    // providers showing (GitHub vs GitLab look similar at 18px). The
    // wrapper spans the row's 1fr column; the trailing checkmark / Connect
    // button still lives in the row's auto-width column.
    return (
      <span className="mkt-modal-integration-brand">
        <img
          src={src}
          alt={name}
          title={name}
          className="mkt-modal-integration-logo"
          width={18}
          height={18}
          loading="lazy"
          onError={(e) => {
            // CDN miss → drop the logo, keep the name (set in the sibling
            // span below). Hide via inline style so the surrounding flex
            // doesn't leave a gap.
            e.currentTarget.style.display = 'none';
          }}
        />
        <span className="mkt-modal-integration-name">{name}</span>
      </span>
    );
  };

  return (
    <div className="mkt-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="mkt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mkt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            {entry.icon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={entry.icon} alt="" className="mkt-modal-icon" />
            ) : (
              <span className="mkt-modal-icon mkt-modal-icon--fallback">
                {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <h2 id="mkt-modal-title" className="mkt-modal-title">{entry.name || entry.slug}</h2>
            <p className="mkt-modal-author">
              By {entry.authorName || 'Unknown'}
              {entry.authorType === 'official' && (
                <span className="mkt-modal-author-badge">Official</span>
              )}
            </p>
            <p className="mkt-modal-tagline">
              {entry.description || entry.tagline}
            </p>

            <div className="mkt-modal-stats" aria-label="Stats">
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                  {/* Stars: clickable when onRate is wired. The POST is
                      upsert by (workflowSlug, userId) on the backend, so
                      re-rating just replaces the prior row + recomputes
                      the histogram atomically. */}
                  <span
                    className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                    role={canRate ? 'radiogroup' : undefined}
                    aria-label={canRate ? 'Rate this workflow' : undefined}
                    onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                  >
                    {[1, 2, 3, 4, 5].map((n) => {
                      const filled = n <= starsFilled;
                      const StarButton = canRate ? 'button' : 'span';
                      const extraProps = canRate
                        ? {
                            type: 'button',
                            role: 'radio',
                            'aria-checked': n === myRatingValue,
                            'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                            disabled: submittingRating,
                            onMouseEnter: () => setHoverRating(n),
                            onFocus: () => setHoverRating(n),
                            onBlur: () => setHoverRating(0),
                            onClick: async () => {
                              if (!entry || submittingRating) return;
                              setSubmittingRating(true);
                              try {
                                await onRate(entry, n);
                              } finally {
                                setSubmittingRating(false);
                              }
                            },
                          }
                        : {};
                      return (
                        <StarButton
                          key={n}
                          className={`mkt-modal-star ${filled ? 'is-filled' : ''}`}
                          {...extraProps}
                        >
                          <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                        </StarButton>
                      );
                    })}
                  </span>
                </div>
                <div className="mkt-modal-stat-label">
                  {myRatingValue > 0
                    ? `You rated ${myRatingValue} · ${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`
                    : `${histogramTotal} ${histogramTotal === 1 ? 'rating' : 'ratings'}`}
                </div>
              </div>
              {entry.category && (
                <div className="mkt-modal-stat">
                  <div className="mkt-modal-stat-value mkt-modal-stat-category">
                    {entry.category}
                  </div>
                  <div className="mkt-modal-stat-label">category</div>
                </div>
              )}
            </div>
          </header>

          {Array.isArray(entry.conversationStarters) && entry.conversationStarters.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Conversation Starters</h3>
              <ul className="mkt-modal-chips">
                {entry.conversationStarters.slice(0, 6).map((cs, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="mkt-modal-chip"
                      // No-op click — these are illustrative, not action
                      // buttons. The keyboard-focusable button shape gives
                      // them tab order in the focus trap without inviting
                      // a click handler we don't have a destination for.
                      tabIndex={0}
                      onClick={() => {}}
                    >
                      {cs}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Required + Optional integrations.
              - Required: deploy gate; if any are missing → Deploy is blocked
                and `blockReason` flashes the unsatisfied rows.
              - Optional: shown only when present; "Connect (optional)" badge,
                no deploy gate. Typical case: `git` skill on a workflow that
                only clones public repos. Section is hidden entirely if the
                workflow has no optional dependencies.
              Both render the same row shape via renderIntegrationRow so
              the visual contract stays identical across the two sections. */}
          {(() => {
            // Inline helper, defined per render so it can close over intStatus
            // + blockReason without prop-drilling. Cheap (called O(rows) per
            // re-render, no virtual-DOM cost beyond the rows themselves).
            const renderIntegrationRow = (entry, connectedList, { optionalSection }) => {
              if (typeof entry === 'string') {
                const id = entry;
                const meta = intStatus.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
                const isConnected = (connectedList || []).includes(id);
                // Flash only applies to REQUIRED rows — optional rows shouldn't
                // turn red even when the deploy gate fires elsewhere.
                const flashCls = !optionalSection && !isConnected && blockReason ? ' is-flashed' : '';
                const rowCls = isConnected ? 'is-connected'
                             : optionalSection ? 'is-optional'
                             : 'is-missing';
                const buttonClass = optionalSection
                  ? 'mkt-modal-integration-connect mkt-modal-integration-connect--optional'
                  : 'mkt-modal-integration-connect';
                const buttonLabel = optionalSection ? 'Connect (optional)' : 'Connect';
                return (
                  <li key={id} className={`mkt-modal-integration-row ${rowCls}${flashCls}`}>
                    <BrandLogo id={id} name={meta.name} />
                    <span className="mkt-modal-integration-state">
                      {isConnected ? (
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          className="mkt-modal-integration-connected-icon"
                          aria-label="Connected"
                        />
                      ) : (
                        <button
                          type="button"
                          className={buttonClass}
                          onClick={(e) => { e.stopPropagation(); handleConnectClick(id, meta.connectPath); }}
                        >
                          {buttonLabel}
                        </button>
                      )}
                    </span>
                  </li>
                );
              }
              // OR group — expand into one row per member.
              const members = Array.isArray(entry?.any) ? entry.any : [];
              if (members.length === 0) return null;
              const matchedGroup = (connectedList || []).find(
                (c) => typeof c === 'object' && Array.isArray(c?.any) && c.any.join(',') === members.join(','),
              );
              const matched = matchedGroup?.connectedMembers || [];
              const groupSatisfied = matched.length > 0;
              return members.map((mid) => {
                const meta = intStatus.integrations?.[mid] || { id: mid, name: mid, connectPath: '/integrations' };
                const isThisConnected = matched.includes(mid);
                // Flash only when this is a REQUIRED group that's unsatisfied.
                const flashCls = !optionalSection && !groupSatisfied && blockReason ? ' is-flashed' : '';
                const rowCls = isThisConnected ? 'is-connected'
                            : (groupSatisfied || optionalSection) ? 'is-optional'
                            : 'is-missing';
                // "(optional)" label for: optional section, OR
                // already-satisfied member of a required OR group.
                const isExtraOption = optionalSection || groupSatisfied;
                const buttonClass = isExtraOption
                  ? 'mkt-modal-integration-connect mkt-modal-integration-connect--optional'
                  : 'mkt-modal-integration-connect';
                const buttonLabel = isExtraOption ? 'Connect (optional)' : 'Connect';
                return (
                  <li
                    key={`${members.join(',')}:${mid}`}
                    className={`mkt-modal-integration-row ${rowCls}${flashCls}`}
                  >
                    <BrandLogo id={mid} name={meta.name} />
                    <span className="mkt-modal-integration-state">
                      {isThisConnected ? (
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          className="mkt-modal-integration-connected-icon"
                          aria-label="Connected"
                        />
                      ) : (
                        <button
                          type="button"
                          className={buttonClass}
                          onClick={(e) => { e.stopPropagation(); handleConnectClick(mid, meta.connectPath); }}
                        >
                          {buttonLabel}
                        </button>
                      )}
                    </span>
                  </li>
                );
              });
            };

            const hasRequired = intStatus && Array.isArray(intStatus.required) && intStatus.required.length > 0;
            const hasOptional = intStatus && Array.isArray(intStatus.optional) && intStatus.optional.length > 0;
            const hasAny = hasRequired || hasOptional;

            // One section, two flavors of row. Required entries render
            // "Connect"; optional render "Connect (optional)" — the badge
            // distinguishes them inline so we don't need a second header.
            // The Deploy gate is independent of this rendering — it reads
            // `missing` (only required) so optional rows never block.
            return (
              <section className="mkt-modal-section" ref={intSectionRef}>
                <div className="mkt-modal-section-title-row">
                  <h3 className="mkt-modal-section-title">Integrations</h3>
                  <a
                    href="/integrations"
                    className="mkt-modal-section-link"
                    aria-label="Manage integrations"
                    title="Manage integrations"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IonIcon icon={exitOutline} aria-hidden="true" />
                  </a>
                </div>
                {intLoading ? (
                  <div className="mkt-skel-stripe" aria-busy="true" aria-label="Loading integrations" />
                ) : intError ? (
                  <p className="mkt-modal-integration-error">Couldn’t load integration status. Deploy will still work; you may need to connect integrations from Settings after install.</p>
                ) : hasAny ? (
                  <ul className="mkt-modal-integrations">
                    {hasRequired && intStatus.required.map((entry) =>
                      renderIntegrationRow(entry, intStatus.connected, { optionalSection: false }))}
                    {hasOptional && intStatus.optional.map((entry) =>
                      renderIntegrationRow(entry, intStatus.optionalConnected, { optionalSection: true }))}
                  </ul>
                ) : (
                  <p className="mkt-modal-integration-none">None</p>
                )}
              </section>
            );
          })()}

          {Array.isArray(entry.capabilities) && entry.capabilities.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Capabilities</h3>
              <ul className="mkt-modal-capabilities">
                {entry.capabilities.map((cap, i) => (
                  <li key={i}>
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mkt-modal-section">
            <h3 className="mkt-modal-section-title">Ratings</h3>
            {histogramTotal === 0 ? (
              <p className="mkt-modal-no-ratings">No ratings yet.</p>
            ) : (
              <div className="mkt-modal-histogram" aria-label={`${histogramTotal} ratings`}>
                {[5, 4, 3, 2, 1].map((stars) => {
                  const count = histogram[stars] || 0;
                  const pct = histogramTotal > 0 ? (count / histogramTotal) * 100 : 0;
                  return (
                    <div key={stars} className="mkt-modal-histogram-row">
                      <span className="mkt-modal-histogram-label">
                        {stars}
                        <IonIcon icon={star} aria-hidden="true" />
                      </span>
                      <div className="mkt-modal-histogram-bar">
                        <div
                          className="mkt-modal-histogram-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="mkt-modal-histogram-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="mkt-modal-footer">
          {isPublic ? (
            /* v1 (unauth) + v2 (authed, no project) share a single
               full-width Deploy. Parent decides what happens — bounce
               to /connect or open the wizard. Name + warm-pool live
               in the wizard / v3 footer (see below). */
            <button
              type="button"
              className="mkt-modal-deploy mkt-modal-deploy--full"
              onClick={handlePublicDeploy}
              disabled={deploying}
              aria-label="Deploy"
            >
              <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
              {deploying ? 'Deploying…' : 'Deploy'}
            </button>
          ) : (
            <MarketplaceWorkflowDeployFooter
              entry={entry}
              projectId={projectId}
              projectName={projectName}
              deploying={deploying}
              onDeploy={handleV3Deploy}
            />
          )}
        </footer>
      </div>

      {/*
        Form-based Connect modals — rendered OUTSIDE the dialog div on
        purpose. IonModal portals to <body> so it doesn't matter for the
        DOM tree, but keeping them at the root of this component keeps
        all "things this modal owns" visible at one glance. Both fire
        onConnected after the integration row is created server-side;
        we bump refreshKey so the checklist re-fetches and the row
        flips to "Connected".
      */}
      <GitlabConnectModal
        isOpen={gitlabModalOpen}
        onClose={() => setGitlabModalOpen(false)}
        onConnected={() => setRefreshKey((k) => k + 1)}
      />
      <LarkConnectModal
        isOpen={larkModalOpen}
        onClose={() => setLarkModalOpen(false)}
        onConnected={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default MarketplaceDetailModal;
